import { useGetCoreLocationsInfiniteQuery } from '@autone/redux/core';
import { sentenceCase } from '@autone/utils';
import { forwardRef } from 'react';

import { VirtualMultiselect } from '../../virtual-multiselect';
import { CoreGeneralFilter } from '../components/CoreGeneralFilter';
import { SERVER_SIDE_SEARCH_LIMIT } from '../constants';
import type {
  CoreGeneralFilterProps,
  CoreGeneralFilterRef,
} from '../CoreGeneralFilters.types';
import { useCoreLocationGeneralFiltersContext } from '../hooks/useCoreLocationGeneralFiltersContext';

export const CoreLocationFilter = forwardRef<
  CoreGeneralFilterRef,
  CoreGeneralFilterProps
>(({ alwaysCommit, searchTerm, className }, ref) => {
  const {
    dimensionAttributeValue,
    setDimensionAttributeValue,
    coreLocationFiltersQueryArg,
  } = useCoreLocationGeneralFiltersContext('geographic.locations');

  const { items, isFetching, isFetchingNextPage, fetchNextPage } =
    useGetCoreLocationsInfiniteQuery(
      {
        query: { limit: SERVER_SIDE_SEARCH_LIMIT },
        body: {
          filters: {
            ...coreLocationFiltersQueryArg,
            geographic: {
              ...coreLocationFiltersQueryArg.geographic,
              search_term: searchTerm,
            },
          },
        },
      },
      {
        selectFromResult: ({ data, ...rest }) => {
          return {
            ...rest,
            items: data?.pages.flatMap((page) => page.items) ?? [],
          };
        },
      },
    );

  return (
    <CoreGeneralFilter
      isFetching={isFetching && !isFetchingNextPage}
      onScrollEnd={fetchNextPage}
      ref={ref}
      selectedItems={dimensionAttributeValue}
      onSelectedItemsChange={setDimensionAttributeValue}
      alwaysCommit={alwaysCommit}
      className={className}
      items={items}
      renderItem={(item) => (
        <VirtualMultiselect.Item>
          <VirtualMultiselect.Checkbox />
          <div className="flex flex-col">
            <span>{sentenceCase(item.payload.description)}</span>
            <span className="text-xs text-grey-60">{item.payload.id}</span>
          </div>
        </VirtualMultiselect.Item>
      )}
    />
  );
});

CoreLocationFilter.displayName = 'CoreLocationFilter';
